import React, { useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Chrono } from 'react-chrono';

import 'react-chrono/dist/style.css';
import './task-timeline.css';

const mount = document.getElementById('taskTimelineRoot');

const readTimelineState = () => {
  const state = window.TDEMAND_TASK_TIMELINE_STATE;
  return {
    loading: Boolean(state?.loading),
    error: String(state?.error || ''),
    events: Array.isArray(state?.events) ? state.events : []
  };
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

const formatTimelineMarker = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const day = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  }).format(date);
  const time = new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
  return `${day}\n${time}`;
};

const formatChangeValue = (value) => {
  if (value == null || value === '') return 'vazio';
  if (typeof value === 'boolean') return value ? 'verdadeiro' : 'falso';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

const eventIcon = (event) => {
  if (event.error_message || event.level === 'error' || event.type === 'error') return 'error';
  if (event.type === 'routing') return 'alt_route';
  if (event.type === 'task_created') return 'add_task';
  return 'edit_note';
};

const timelineTheme = () => {
  const styles = getComputedStyle(document.documentElement);
  const css = (name, fallback) => styles.getPropertyValue(name).trim() || fallback;
  const primary = css('--primary', '#7c3aed');
  const primaryHover = css('--primary-strong', '#6d28d9');
  const primarySoft = css('--primary-soft', '#ede9fe');
  const primaryMid = '#a78bfa';
  const text = css('--text', '#111827');
  const muted = css('--muted', '#64748b');
  const surface = css('--surface', '#ffffff');
  return {
    primary,
    secondary: primaryMid,
    cardBgColor: surface,
    cardDetailsBackGround: primarySoft,
    cardDetailsColor: text,
    cardMediaBgColor: primarySoft,
    cardForeColor: text,
    cardTitleColor: primary,
    cardSubtitleColor: muted,
    detailsColor: muted,
    iconBackgroundColor: primary,
    iconColor: '#ffffff',
    nestedCardBgColor: surface,
    nestedCardDetailsBackGround: primarySoft,
    nestedCardDetailsColor: text,
    nestedCardSubtitleColor: muted,
    nestedCardTitleColor: primary,
    textColor: text,
    titleColor: muted,
    titleColorActive: primary,
    toolbarBgColor: primarySoft,
    toolbarBtnBgColor: surface,
    toolbarTextColor: text,
    timelineBgColor: surface,
    buttonHoverBgColor: primarySoft,
    buttonActiveBgColor: primary,
    buttonActiveIconColor: '#ffffff',
    buttonBorderColor: primaryMid,
    buttonHoverBorderColor: primaryHover,
    buttonActiveBorderColor: primary,
    shadowColor: 'rgba(124, 58, 237, 0.14)',
    glowColor: 'rgba(124, 58, 237, 0.22)',
    searchHighlightColor: primarySoft,
    darkToggleActiveBgColor: primary,
    darkToggleActiveIconColor: '#ffffff',
    darkToggleActiveBorderColor: primary,
    darkToggleGlowColor: 'rgba(124, 58, 237, 0.22)'
  };
};

function TimelineCard({ event }) {
  const changes = Array.isArray(event.changes) ? event.changes : [];
  return (
    <article className={`task-timeline-card ${event.error_message ? 'has-error' : ''}`}>
      <div className="task-timeline-card-title">
        <span className="material-symbols-rounded" aria-hidden="true">{eventIcon(event)}</span>
        <div>
          <strong>{event.title || 'Evento da task'}</strong>
          <small>{formatDateTime(event.date)} · {event.actor_type || 'Sistema'}: {event.actor || 'Sistema'}</small>
        </div>
      </div>
      {event.summary ? <p>{event.summary}</p> : null}
      {event.error_message ? (
        <div className="task-timeline-error">
          <span className="material-symbols-rounded" aria-hidden="true">report</span>
          {event.error_message}
        </div>
      ) : null}
      {changes.length ? (
        <div className="task-timeline-changes">
          <div className="task-timeline-changes-head">
            <span className="material-symbols-rounded" aria-hidden="true">difference</span>
            <strong>{changes.length} mudança{changes.length === 1 ? '' : 's'} registrada{changes.length === 1 ? '' : 's'}</strong>
          </div>
          <div className="task-timeline-changes-list">
            {changes.map((change, index) => (
              <div className="task-timeline-change" key={`${change.field || 'campo'}-${index}`}>
                <b>{change.field || 'campo'}</b>
                <span>
                  {formatChangeValue(change.before)}
                  {' -> '}
                  {formatChangeValue(change.after)}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </article>
  );
}

function TaskTimeline() {
  const [state, setState] = useState(readTimelineState);
  const [theme, setTheme] = useState(timelineTheme);

  useEffect(() => {
    const receiveTimeline = (event) => {
      setState({
        loading: Boolean(event.detail?.loading),
        error: String(event.detail?.error || ''),
        events: Array.isArray(event.detail?.events) ? event.detail.events : []
      });
    };
    window.addEventListener('tdemand:task-timeline', receiveTimeline);
    setState(readTimelineState());
    return () => window.removeEventListener('tdemand:task-timeline', receiveTimeline);
  }, []);

  useEffect(() => {
    const observer = new MutationObserver(() => setTheme(timelineTheme()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  const items = useMemo(() => state.events.map((event) => ({
    title: formatTimelineMarker(event.date),
    cardTitle: event.title || 'Evento da task',
    cardSubtitle: `${event.actor_type || 'Sistema'}: ${event.actor || 'Sistema'}`
  })), [state.events]);

  if (state.loading) {
    return (
      <div className="task-timeline-empty">
        <span className="material-symbols-rounded" aria-hidden="true">hourglass_top</span>
        Carregando linha do tempo da task...
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="task-timeline-empty is-error">
        <span className="material-symbols-rounded" aria-hidden="true">warning</span>
        {state.error}
      </div>
    );
  }

  if (!state.events.length) {
    return (
      <div className="task-timeline-empty">
        <span className="material-symbols-rounded" aria-hidden="true">timeline</span>
        Nenhuma mudança foi registrada para esta task ainda.
      </div>
    );
  }

  return (
    <div className="task-timeline-shell">
      <Chrono
        items={items}
        mode="vertical"
        disableToolbar
        theme={theme}
        classNames={{ card: 'td-chrono-card' }}
      >
        {state.events.map((event) => (
          <TimelineCard event={event} key={event.id || `${event.type}-${event.date}`} />
        ))}
      </Chrono>
    </div>
  );
}

if (mount) {
  createRoot(mount).render(<TaskTimeline />);
}
